import GoogleAnalytics from '@disco/api/ga'
import Intercom from '@disco/api/intercom'
import type { Location } from '@disco/api/mixpanel'
import api from '@disco/api/server'
import {
  APP_REQUEST_COMPLETED,
  APP_REQUEST_STARTED,
  APP_REQUEST_RESET,
  BUSINESS_DATA_LOAD,
  BUSINESS_SETTINGS_LOAD,
  BUSINESS_SETTINGS_UPDATE,
  BUSINESS_SETTINGS_UPDATE_FAIL,
  BUSINESS_SETTINGS_UPDATE_PROGRESS,
  BUSINESS_SETTINGS_UPDATE_SUCCESS,
  BUSINESS_SWITCH,
  PRELOAD_DATA_ON_LOGIN,
  SUBSCRIPTION_LOAD,
  SUBSCRIPTION_LOAD_FAIL,
  SUBSCRIPTION_LOAD_SUCCESS,
  USER_AUTOLOGIN,
  USER_AUTOLOGIN_ERROR,
  USER_AUTOLOGIN_SUCCESS,
  USER_BUSINESS_LIST_RESET,
  USER_CLEAR_MFA_STATE,
  USER_LOGIN,
  USER_LOGIN_ERROR_CLEAR,
  USER_LOGIN_FAIL,
  USER_LOGIN_REQUIRE_BUSINESS_SELECT,
  USER_LOGIN_REQUIRE_MFA,
  USER_LOGIN_SUCCESS,
  USER_LOGOUT,
  USER_PASSWORD_CHANGE,
  USER_PASSWORD_CHANGE_FAIL,
  USER_PASSWORD_CHANGE_SUCCESS,
  USER_PRELOADED,
  USER_SETTINGS_UPDATE,
  USER_SETTINGS_UPDATE_FAIL,
  USER_SETTINGS_UPDATE_SUCCESS,
  USER_URL_CHECK,
  USER_URL_CHECK_FAIL,
  USER_URL_CHECK_SUCCESS,
  USER_UPDATED_MOBILE_BUSINESS_LIST,
  APP_URL_REDIRECT,
} from '@disco/constants/ActionTypes'
import { __REACT_NATIVE__, userTokenKey } from '@disco/constants/environment'
import { TOUR_EVENT } from '@disco/constants/EventTypes'
import {
  INBOX_TYPES,
  LIBRARY_TYPES,
  PERSONAL_INBOX,
  PERSONAL_LIBRARY,
  TEAM_LIBRARY,
} from '@disco/constants/GroupTypes'
import { loadThemes } from '@disco/state-management/reducers/themes'
import {
  getBusinessSettings,
  getMfaAuthEnabled,
  getUser,
  isNativeTagsEnabled,
} from '@disco/state-management/selectors/user'
import { getCookie } from '@disco/utils/business-cookie'
import { parseServerError } from '@disco/utils/captureError'
import { setDatadogUser } from '@disco/utils/datadog'
import { explainServerLoginErrorAsString } from '@disco/utils/error'
import { pushUINotification } from '@disco/utils/notifications'
import { getUrlHostname } from '@disco/utils/url'
import captureError from '@disco/utils/captureError'
import Storage from 'disco-storage'
import get from 'lodash/get'
import omit from 'lodash/omit'
import dayjs from 'dayjs'
import NavigationService from '@disco/state-management/NavigationService'
import type {
  Business,
  BusinessData,
  BusinessDataPatchRequest,
  PrivateMetadata,
} from 'types/business'
import type { UINotification } from 'types/notification'
import type { Thunk } from 'types/redux'
import type { LoginErrorMessageCode } from 'types/errors'
import type { LoginDetails, LoginErrorCode, Settings, UserId } from 'types/user'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { clientLibraryListGetAll } from '@disco/state-management/actions/client-library-list-actions'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import {
  loadUserDefaultLibrary,
  openPrivacyPolicyDialog,
} from '@disco/state-management/actions/ExplorerActions'
import { loadGroups } from '@disco/state-management/actions/GroupActions'
import {
  initShortlists,
  startSavingShortlistState,
} from '@disco/state-management/actions/ShortlistActions'
import type { CookieConsentContextType } from 'types/cookie-consent'
import { fetchRightsHolders } from '@disco/api/rights-data'

/**
 * Custom error class for login-related errors.
 */
export class LoginError extends Error {
  code: LoginErrorCode
  errorMessageCode?: LoginErrorMessageCode | null
  errorMessageParams?: Record<string, string | number> | null
  serverDescription?: string | null

  /**
   * Creates a new LoginError instance.
   * @param code - The specific login error code
   * @param message - Human-readable error message
   * @param options.errorMessageCode - Which translated message to render
   * @param options.errorMessageParams - Interpolation values for that message
   * @param options.serverDescription - Untranslated server detail to render too
   */
  constructor(
    code: LoginErrorCode,
    message: string,
    options: {
      errorMessageCode?: LoginErrorMessageCode | null
      errorMessageParams?: Record<string, string | number> | null
      serverDescription?: string | null
    } = {},
  ) {
    super()
    this.message = message
    this.code = code
    this.errorMessageCode = options.errorMessageCode
    this.errorMessageParams = options.errorMessageParams
    this.serverDescription = options.serverDescription
  }
}

function isBriefsRelatedPath(path: string | null | undefined): boolean {
  if (!path) return false
  return (
    path.startsWith('/briefs') ||
    path.startsWith('/brief-editor') ||
    path.startsWith('/brief/')
  )
}

function normalizeNativeTags(nativeTags) {
  const entities = {
    nativeCategories: {},
    nativeTags: {},
  }

  if (nativeTags) {
    return nativeTags.reduce((accumulator, tag) => {
      const result = accumulator
      const { id, category_id, category_name } = tag

      if (result.nativeCategories[category_id]) {
        result.nativeCategories[category_id].native_tags.push(id)
      } else {
        result.nativeCategories[category_id] = {
          id: category_id,
          name: category_name,
          native_tags: [id],
        }
      }

      result.nativeTags[id] = omit(tag, 'category_name')
      return result
    }, entities)
  } else {
    return entities
  }
}

/* Allows us to present loading progress to the user on launch */
const runTrackedPromise = <T>(
  promise: Promise<T>,
  dispatch: any,
): Promise<T> => {
  dispatch({ type: APP_REQUEST_STARTED })
  return promise.then(args => {
    dispatch({ type: APP_REQUEST_COMPLETED })
    return args
  })
}

function preloadDataOnLogin(): Thunk {
  return async (dispatch, getState) => {
    try {
      window._loadBusinessData = () => dispatch(loadBusinessData())

      const [
        tourEvents,
        tags,
        nativeTags,
        playlistTags,
        playlistTypes,
        metadataKeys,
        pro,
        publishers,
        rightsholders,
      ] = await Promise.all([
        runTrackedPromise(api.getEventsByType(TOUR_EVENT), dispatch),
        runTrackedPromise(api.getTags(), dispatch),

        isNativeTagsEnabled(getState())
          ? runTrackedPromise(api.getNativeTags(), dispatch)
          : null,

        runTrackedPromise(api.getPlaylistTags(), dispatch),
        runTrackedPromise(api.getPlaylistTypes(), dispatch),
        runTrackedPromise(api.getMetadataKeys(), dispatch),
        runTrackedPromise(api.getPRO(), dispatch),
        runTrackedPromise(api.getPublishers(), dispatch),
        runTrackedPromise(fetchRightsHolders({}), dispatch),

        // We just need to trigger, we do not need the result so placed at the end

        runTrackedPromise(
          dispatch(clientLibraryListGetAll({ rethrowError: true })),
          dispatch,
        ),
        runTrackedPromise(
          dispatch(loadGroups({ rethrowError: true })),
          dispatch,
        ),
      ])

      const rightsholderPublishers = rightsholders.results
        .filter(rh => rh.entity_type === 'Publisher')
        .map(pub => ({
          id: pub.id,
          name: pub.title,
          entity_details: pub.entity_details,
        }))
      const rightsholderPro = rightsholders.results
        .filter(rh => rh.entity_type === 'PRO')
        .map(pro => ({
          id: pro.id,
          name: pro.title,
          entity_details: pro.entity_details,
        }))

      // TODO: move back to Promise.all once https://disco-team.atlassian.net/browse/DISCO-6560 will be done
      // use .unwrap() to rethrow error to cause the catch block to be executed
      dispatch(loadThemes())
      dispatch({
        type: PRELOAD_DATA_ON_LOGIN,
        entities: {
          tourEvents,
          tags,
          playlistTags: playlistTags.reduce(
            (accumulator, tag) => ({ ...accumulator, [tag.id]: tag }),
            {},
          ),
          playlistTypes: playlistTypes.results,
          metadataKeys: metadataKeys.map(k => {
            // selectors should get all the data for related values from
            // business metadata
            if (k.type === 'dropdown_multi' || k.type === 'dropdown_single')
              return omit(k, 'allow_create', 'values')
            return k
          }),
          pro,
          publishers,
          rightsholderPro,
          rightsholderPublishers,
          ...normalizeNativeTags(nativeTags),
        },
      })
      if (!__REACT_NATIVE__) dispatch(openPrivacyPolicyDialog())
    } catch (e) {
      captureError(new Error('Failed to preload data on login'), {
        extra: {
          originalError: e,
        },
      })
      throw e
    }
  }
}

// TODO: Not sure if this should be moved to selectors or utilities
function getLibraryName(group) {
  switch (group.group_type) {
    case TEAM_LIBRARY:
      return 'Browse'

    case PERSONAL_LIBRARY:
      return 'Starred'

    default:
      return group.name
  }
}

function generateLibraries(userGroups) {
  return userGroups
    .filter(group => {
      const { group_type } = group
      return (
        group_type === TEAM_LIBRARY ||
        group_type === PERSONAL_LIBRARY ||
        group_type === PERSONAL_INBOX
      )
    })
    .map(group => {
      const baseUrl = `${window.host}/api/browse/?type=playlist`
      let urlParams = ''

      if (group && INBOX_TYPES.includes(group.group_type)) {
        urlParams = baseUrl.includes('?') ? '&share=1' : '?share=1'
      }

      return {
        id: group.id,
        name: group.name,
        internal_name: group.internal_name,
        image: group.image,
        group_type: group.group_type,
        url: group.url,
        panes: [
          {
            url: baseUrl + urlParams,
            name:
              FRONTEND_VERSION === 'v2' ? getLibraryName(group) : group.name,
            internal_name: group.internal_name,
            index: 0,
            groupId: group.id,
            groupIds:
              FRONTEND_VERSION === 'v2' && group.group_type == TEAM_LIBRARY // TEAM_LIBRARY in v2 acts as Browse and should include everything
                ? userGroups
                    .filter(({ group_type }) =>
                      LIBRARY_TYPES.includes(group_type),
                    )
                    .map(({ id }) => id)
                : undefined,
            groupType: group.group_type,
            type: 'searchResults',
          },
        ],
      }
    })
}

const reportUserGroups = user => {
  if (
    user.groups.filter(g => LIBRARY_TYPES.includes(g.group_type)).length < 2
  ) {
    captureError(new Error('User does not have team or personal library'), {
      extra: {
        level: 'warning',
        user,
      },
    })
  }
}

/**
 * Attempts to automatically log in a user using stored credentials.
 * @param returnPath - Path to redirect to after successful login (default: '/')
 * @param host - Optional host to authenticate against
 * @returns Thunk that handles the autologin process
 */
export function autologinUser(returnPath = '/', host?: string): Thunk {
  return async (dispatch, getState) => {
    if (getState().user.autologgingIn) return
    dispatch({ type: APP_REQUEST_RESET })
    dispatch({ type: USER_AUTOLOGIN })

    if (host) {
      api.host = host
    }

    const csrfToken = await runTrackedPromise(api.getCSRFToken(), dispatch)
    const [constance, user] = await Promise.all([
      runTrackedPromise(api.getConstanceConfig(), dispatch),
      runTrackedPromise(api.getUserDetails(), dispatch),
    ])
    let userToken = await Storage.get(userTokenKey)

    if (!userToken) {
      if (!user.id) {
        dispatch({
          type: USER_AUTOLOGIN_ERROR,
          reason: 'NO_LOCAL_STORAGE_TOKEN',
        })
        return
      }

      if (user.status === 'error') {
        // TODO: check if it can be removed
        Storage.set(userTokenKey, null)
      }

      userToken = {
        csrfToken,
      }
      Storage.set(userTokenKey, userToken)
    }

    try {
      const company = await runTrackedPromise(
        api.getCurrentBusiness(),
        dispatch,
      )

      setDatadogUser(user.id)
      reportUserGroups(user)
      const userObj = {
        ...user,
        libraries: generateLibraries(user.groups),
        business: company,
        constance,
        csrfToken: csrfToken,
      }
      dispatch({
        type: USER_AUTOLOGIN_SUCCESS,
        result: userObj,
        meta: !__REACT_NATIVE__
          ? {
              mixpanel: {
                event: 'Login',
              },
            }
          : {},
      })

      await runTrackedPromise(dispatch(loadBusinessSubscription()), dispatch)

      if (!isBriefsRelatedPath(returnPath)) {
        dispatch(loadUserDefaultLibrary(userObj))
      }

      dispatch(
        loadLocalShortlistState(
          user.id,
          __REACT_NATIVE__ ? company.id : undefined,
        ),
      )

      await dispatch(preloadDataOnLogin())

      dispatch(redirectTo(returnPath))

      if (company.stats.total_track_count < 100000) {
        dispatch(loadBusinessData())
      }

      checkMfaSetup(getState(), dispatch, userToken)

      if (!__REACT_NATIVE__) {
        // experiment https://app.asana.com/0/0/1199239685440044/f
        setTimeout(async () => {
          const start = Date.now()
          await api.ping()
          const timing = Date.now() - start
          console.log('ping', timing)
          dispatch(
            sendMixpanelEvent('Ping', {
              Time: timing,
            }),
          ) // wait quiet moment for pinging
        }, 5000)
      }
    } catch (error) {
      dispatch(sendGenericError(error))
      dispatch({
        type: USER_AUTOLOGIN_ERROR,
        reason: 'INVALID_SESSION_OR_NETWORK_ERROR',
        error,
      })
    }
  }
}
type LoginOptions = {
  // TODO: move more fields into options,
  location?: Location
  cookieConsentContext?: CookieConsentContextType
  loginType?: 'saml' | 'password'
  loginToken?: string | Record<string, any> | null
}
/**
 * Loads user data and initializes the application state after authentication.
 * @param returnPath - Path to redirect to after loading, or false to not redirect
 * @param host - Optional host for the API
 * @param mfaUsed - Whether multi-factor authentication was used
 * @param options - Additional login options including location and consent context
 * @returns Thunk that loads user data and handles login success/failure
 */
export function loadUser(
  returnPath: (string | null | undefined) | false,
  host: string | null | undefined,
  mfaUsed: boolean,
  options: LoginOptions = {},
): Thunk {
  return async dispatch => {
    dispatch({
      type: USER_LOGIN,
    })

    try {
      if (host) {
        api.host = host
      }

      const csrfToken = await api.getCSRFToken()
      const [constance, user, company] = await Promise.all([
        api.getConstanceConfig(),
        api.getUserDetails(),
        options.location === 'Libraries'
          ? api.getCurrentBusinessPublic()
          : api.getCurrentBusiness(),
      ])

      if (user.id) {
        setDatadogUser(user.id)
      }

      let explorerGroups

      if (options.location !== 'Libraries') {
        reportUserGroups(user)
        explorerGroups = generateLibraries(user.groups) // TODO: rename generateLibraries
      }

      const userObj = {
        ...user,
        libraries: explorerGroups,
        business: company,
        csrfToken: csrfToken,
        constance,
      }
      dispatch({
        type: USER_LOGIN_SUCCESS,
        result: userObj,
        meta: !__REACT_NATIVE__
          ? {
              mixpanel: {
                event: 'Login',
              },
            }
          : {},
        mfaUsed,
        loginType: options.loginType,
      })
      await dispatch(loadBusinessSubscription())

      if (typeof returnPath !== 'string' || !isBriefsRelatedPath(returnPath)) {
        dispatch(loadUserDefaultLibrary(userObj))
      }

      dispatch(
        loadLocalShortlistState(
          user.id,
          __REACT_NATIVE__ ? company.id : undefined,
        ),
      )

      if (options.location !== 'Libraries') {
        await dispatch(preloadDataOnLogin())
      }

      await dispatch(redirectTo(returnPath))

      if (options.location !== 'Libraries') {
        if (company.stats.total_track_count < 100000) {
          await dispatch(loadBusinessData())
        }
      }

      dispatch({
        type: USER_PRELOADED,
      })
      return userObj
    } catch (error) {
      console.error('loadUser error', error)
      captureError(error)
      return dispatch({
        type: USER_LOGIN_FAIL,
        error,
      })
    }
  }
}

const flushLocalState = (dispatch: any) => {
  Intercom.shutdown()
  GoogleAnalytics.reset()
  Storage.set(userTokenKey, null)
  setDatadogUser(null)
  api.resetBrowserInstanceId()
  dispatch({
    type: USER_LOGOUT,
    meta: {
      mixpanel: {
        event: 'Logout',
      },
    },
  })
}

const requestServerLogout = async () => {
  try {
    await api.getUserLogout()
  } catch (error) {
    // 401/403 means the server session is already gone. Local cleanup must still run.
    if (error?.status !== 401 && error?.status !== 403) {
      captureError(error, { description: 'Logout request failed' })
    }
  }
}

/**
 * Logs out the current user and cleans up application state.
 * @param sessionExpired - Whether the logout is due to session expiration
 * @returns Thunk that handles user logout and redirect
 */
export function logoutUser(sessionExpired = false): Thunk {
  return async dispatch => {
    await requestServerLogout()
    flushLocalState(dispatch)
    NavigationService.push(
      sessionExpired ? '/login?sessionExpired=1' : '/login',
    )
  }
}

/**
 * Logs out the current user from catalogs and cleans up application state without navigation.
 * @returns Thunk that handles user logout
 */
export function logoutUserFromCatalogs(): Thunk {
  return async dispatch => {
    await requestServerLogout()
    flushLocalState(dispatch)
  }
}

/**
 * Deletes the current user account and cleans up application state.
 * @returns Thunk that handles user account deletion
 */
export const deleteUser = (): Thunk => async (dispatch, getState) => {
  const { user } = getState()
  const numberOfUsers = get(user, 'business.stats.number_of_users', 0)

  try {
    await api.deleteUser()
    flushLocalState(dispatch)

    if (numberOfUsers > 1) {
      NavigationService.push('/profile-deleted')
    } else {
      NavigationService.push('/profile-deleted?sole=1')
    }
  } catch (e) {
    dispatch(sendGenericError(e))
    pushUINotification({
      message: parseServerError(e.body),
      level: 'error',
    })
  }
}
/**
 * Changes the user's password.
 * @param newPassword - The new password to set
 * @param oldPassword - The current password for verification (optional)
 * @returns Thunk that handles password change with validation
 */
export function changePassword(
  newPassword: string,
  oldPassword: string | null | undefined = undefined,
): Thunk {
  return async dispatch => {
    dispatch({
      type: USER_PASSWORD_CHANGE,
    })

    try {
      const response = await api.changePassword(newPassword, oldPassword)

      if (response.status === 'error') {
        return dispatch({
          type: USER_PASSWORD_CHANGE_FAIL,
          error: new LoginError(
            'OLD_PASSWORD_INCORRECT',
            'Old password is incorrect',
          ),
        })
      }

      dispatch({
        type: USER_PASSWORD_CHANGE_SUCCESS,
      })
      dispatch(sendMixpanelEvent('Change password in settings'))
      pushUINotification({
        message: 'Password changed!',
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: explainServerLoginErrorAsString(error),
        level: 'error',
      })
      return dispatch({
        type: USER_PASSWORD_CHANGE_FAIL,
        error,
      })
    }
  }
}
/**
 * Resets user password during password recovery process.
 * Differs from changePassword - requires referrer and is used in password recovery flow.
 * @param password - The new password to set
 * @param referer - The referrer URL for password recovery
 * @returns Thunk that resets the user password
 */
export function resetPassword(password: string, referer: string): Thunk {
  return async dispatch => {
    dispatch({
      type: USER_PASSWORD_CHANGE,
    })

    try {
      api.csrfToken = await api.getCSRFToken()
      const response = await api.postUserAuth({
        new_password: password,
        referer: referer,
      })

      if (response.status === 'error' && response.reason !== 'mfa_required') {
        return dispatch({
          type: USER_PASSWORD_CHANGE_FAIL,
        })
      }

      dispatch({
        type: USER_PASSWORD_CHANGE_SUCCESS,
      })
    } catch (error) {
      return dispatch({
        type: USER_PASSWORD_CHANGE_FAIL,
        error,
      })
    }
  }
}
/**
 * Checks if a login URL is valid and accessible.
 * @param url - The URL to validate
 * @returns Thunk that validates the login URL
 */
export function checkLoginUrl(url: string): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: USER_URL_CHECK,
      })
      await api.checkLoginUrl(url)
      dispatch({
        type: USER_URL_CHECK_SUCCESS,
      })
    } catch (error) {
      dispatch({
        type: USER_URL_CHECK_FAIL,
        error,
      })
    }
  }
}
type ErrorResponse = {
  reason: string
  business_name?: string
  description?: string // `description` is typically not present
}
/**
 * Converts a server error response into a standardized LoginError.
 * @param errorResponse - Error response from the server
 * @returns LoginError with appropriate code and message
 */
export const getLoginErrorFromServerResponse = (
  errorResponse: ErrorResponse,
): LoginError => {
  const { reason, business_name, description } = errorResponse

  if (reason === 'business_user_inactive_for_long_enough') {
    const admin = business_name ? `${business_name} admin` : 'admin'
    return new LoginError(
      'USER_INACTIVE_FOR_LONG_TIME',
      `Your account has been locked due to inactivity. To have your account unlocked, please contact the ${admin} on your DISCO account and if you are eligible, they will confirm to DISCO that your account will be reactivated. If you do not know who your administrator is, please reach out to support@disco.ac.`,
      {
        errorMessageCode: 'USER_INACTIVE_FOR_LONG_TIME',
        errorMessageParams: business_name
          ? { businessName: business_name }
          : null,
      },
    )
  }

  const formattedDescription = (description || '').replace(/'/g, '’')
  const serverDescription = formattedDescription || null

  switch (reason) {
    case 'login_limit_exceeded':
      return new LoginError(
        'LOGIN_LIMIT_EXCEEDED',
        'Account is locked out due to too many invalid login attempts',
        { errorMessageCode: 'LOGIN_LIMIT_EXCEEDED' },
      )

    case 'captcha_required':
    case 'captcha_invalid':
      return new LoginError(
        'CAPTCHA_REQUIRED',
        'We have detected multiple unsuccessful login attempts. To protect your account from brute force attacks, we’ve enabled Captcha. Please check your credentials and try to login again.',
        { errorMessageCode: 'CAPTCHA_REQUIRED' },
      )

    case 'invalid_mfa_code':
      return new LoginError(
        'INVALID_MFA_CODE',
        `The code you have entered is invalid, please enter a valid code.`,
        { errorMessageCode: 'INVALID_MFA_CODE' },
      )

    case 'client_library_access_requested':
      return new LoginError(
        'ACCESS_REQUESTED',
        'Access requested. Your request for access has been sent. You’ll receive an email when your access is approved.',
        { errorMessageCode: 'ACCESS_REQUESTED' },
      )

    case 'client_library_awaiting_access':
      return new LoginError(
        'AWAITING_ACCESS',
        'Awaiting access. You’ll receive an email when your access is approved.',
        { errorMessageCode: 'AWAITING_ACCESS' },
      )

    case 'login_rate_exceeded':
      return new LoginError(
        'PASSWORD_INCORRECT',
        formattedDescription ||
          `We’ve detected numerous login failures. And as a security precaution we’ve limited login access.`,
        formattedDescription ? {} : { errorMessageCode: 'LOGIN_RATE_EXCEEDED' },
      )

    case 'plain_login_disabled':
      return new LoginError(
        'PASSWORD_INCORRECT',
        'Sorry, login with password is disabled.',
        { errorMessageCode: 'PLAIN_LOGIN_DISABLED' },
      )

    case 'login_invalid':
      return new LoginError(
        'PASSWORD_INCORRECT',
        'Sorry, either your username or password is incorrect. Please try again.',
        { errorMessageCode: 'LOGIN_INVALID' },
      )

    // Sent by the add-to-DISCO endpoint for bad credentials. The code stays as
    // the raw reason because packages/cl/pages/add-to-disco.tsx matches on it.
    case 'wrong_username_or_password':
      return new LoginError(
        reason,
        'Sorry, either your username or password is incorrect. Please try again.',
        { errorMessageCode: 'LOGIN_INVALID' },
      )

    // An unrecognised reason is not evidence of bad credentials, so the
    // translated copy stays generic and the server description carries whatever
    // detail there is. `message` keeps its old value for consumers that have not
    // adopted errorMessageCode.
    default:
      return new LoginError(
        reason,
        formattedDescription ||
          'Sorry, either your username or password is incorrect. Please try again.',
        { errorMessageCode: 'UNKNOWN_LOGIN_ERROR', serverDescription },
      )
  }
}
/**
 * Loads and sets up multi-factor authentication methods for the user.
 * @returns Thunk that loads MFA methods and redirects to setup if needed
 */
export const loadMfaMethods = (): ((dispatch: any) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any) => {
    try {
      const mfaMethods = await api.getMfaAuthMethods()
      const totpMethods = mfaMethods.filter(m => m.method === 'time_based')
      let totpMethod: any = null

      if (totpMethods.length === 0) {
        totpMethod = await api.createMfaAuthMethod('time_based')
      } else {
        totpMethod = totpMethods[0]
      }

      if (totpMethod && !totpMethod.verified) {
        const originalUrl =
          window.location.pathname === '/'
            ? '/'
            : window.location.pathname + window.location.search
        NavigationService.push('/mfa-setup', {
          state: {
            totpMethod,
            returnTo: originalUrl,
          },
        })
      }
    } catch (error) {
      dispatch(sendGenericError(error))
    }
  }
}

const checkMfaSetup = (state, dispatch, userToken?: any) => {
  const user = getUser(state)
  const mfaEnabled = getMfaAuthEnabled(state) || user.is_support_user

  if (getCookie('mfa_passed')) {
    Storage.set(userTokenKey, { ...userToken, loggedInWithMFA: true })
  }

  if (
    mfaEnabled &&
    !user.loggedInWithMFA &&
    !(userToken || {}).loggedInWithMFA &&
    !getCookie('mfa_passed')
  ) {
    dispatch(loadMfaMethods())
  }
}

function redirectTo(returnPath: false | (string | null | undefined)) {
  return dispatch => {
    if (returnPath === false) {
      // PostInvites keep redirected user without direct
      return
    } else if (
      returnPath &&
      returnPath.startsWith &&
      (returnPath.startsWith('/discoadmin/') ||
        returnPath.startsWith('/oauth/'))
    ) {
      // admin login
      // risk of open redirect vurnerability here
      // @ts-expect-error - fix later
      window.location = returnPath
    } else {
      // normal case login
      NavigationService.replace(returnPath || '/')
      dispatch({ type: APP_URL_REDIRECT })
    }
  }
}

/**
 * Authenticates a user with the provided login credentials.
 * @param loginDetails - User login credentials (email/password)
 * @param returnPath - Path to redirect to after successful login, or false to not redirect
 * @param host - Optional host for the API
 * @param options - Additional login options including location and consent context
 * @returns Thunk that handles user authentication and login flow
 */
export function loginUser(
  loginDetails: LoginDetails,
  returnPath: (string | null | undefined) | false,
  host?: string | null | undefined,
  options: LoginOptions = {},
): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: USER_LOGIN,
    })

    if (options.cookieConsentContext) {
      if (
        !options.cookieConsentContext.consent.necessary &&
        options.cookieConsentContext.bannerRequired
      ) {
        return dispatch({
          type: USER_LOGIN_FAIL,
          error: {
            message:
              'Login failed. You may have an ad blocker that is causing issues. Please temporarily disable your ad blocker and try again.',
          },
        })
      }
    }

    try {
      if (host) {
        api.host = host
      }

      api.csrfToken = await api.getCSRFToken()
      const userAuthResp = await api.postUserAuth(loginDetails)

      if (userAuthResp.status !== 'success' && !userAuthResp.token) {
        if (userAuthResp.reason === 'mfa_required') {
          return dispatch({
            type: USER_LOGIN_REQUIRE_MFA,
            mfaType: userAuthResp.mfa_type,
          })
        }

        Storage.set(userTokenKey, null)
        return dispatch({
          type: USER_LOGIN_FAIL,
          error: getLoginErrorFromServerResponse(userAuthResp),
        })
      }

      let userToken: any = null

      // JWT auth
      if (userAuthResp.token) {
        userToken = {
          jwttoken: userAuthResp.token,
        }
        api.jwtToken = userAuthResp.token
      } else {
        // api doesn't always return session object, try to construct it
        // by yourself for now
        userToken = userAuthResp.session
          ? userAuthResp.session
          : {
              csrftoken: api.csrfToken,
            }
      }

      if (!userAuthResp.redirect_to) {
        await dispatch(
          loadUser(returnPath, host, !!(loginDetails as any).mfa_code, options),
        )
      }
      // enforce 2fa setup only for normal login flow,
      // for invites user needs to set password first
      if (!('referer' in loginDetails)) checkMfaSetup(getState(), dispatch)
      // keep information if user logged in with MFA so we save on API call later
      const resultToken = (loginDetails as any).mfa_code
        ? { ...userToken, loggedInWithMFA: true }
        : userToken
      Storage.set(userTokenKey, resultToken)
      if (userAuthResp.redirect_to) {
        window.location.href = userAuthResp.redirect_to
      }
    } catch (error) {
      Storage.set(userTokenKey, null)
      return dispatch({
        type: USER_LOGIN_FAIL,
        error,
      })
    }
  }
}
/**
 * Resets the user's business list to its initial state.
 * @returns Action object to reset user business list
 */
export const resetUserBusinessList = (): {
  type: string
} => ({
  type: USER_BUSINESS_LIST_RESET,
})
/**
 * Clears the multi-factor authentication state.
 * @returns Action object to clear MFA state
 */
export const clearMfaState = (): {
  type: string
} => {
  return {
    type: USER_CLEAR_MFA_STATE,
  }
}
/**
 * Triggers a business switch operation.
 * @returns Action object to switch business
 */
export const switchBusiness = (): {
  type: string
} => ({
  type: BUSINESS_SWITCH,
})
/**
 * Handles login for users with multiple business accounts.
 * @param loginDetails - User login credentials
 * @param returnPath - Path to redirect to after login, or false to not redirect
 * @param host - Optional host for the API
 * @param resetStateAfterLogin - Whether to reset application state after login
 * @returns Thunk that handles multi-account login flow
 */
// Used for mobile login
export function multiAccountLogin(
  loginDetails: LoginDetails,
  returnPath: (string | null | undefined) | false,
  host?: string,
  resetStateAfterLogin?: boolean,
): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: USER_LOGIN,
    })

    try {
      const userAuthResp = await api.multiAccountLogin(loginDetails)

      if (
        userAuthResp.status === 'partial' &&
        userAuthResp.available_businesses?.length
      ) {
        return dispatch({
          type: USER_LOGIN_REQUIRE_BUSINESS_SELECT,
          businesses: userAuthResp.available_businesses,
        })
      }

      if (userAuthResp.status !== 'success' && !userAuthResp.jwt_token) {
        if (userAuthResp.reason === 'mfa_required') {
          return dispatch({
            type: USER_LOGIN_REQUIRE_MFA,
            mfaType: userAuthResp.mfa_type,
          })
        }
        Storage.set(userTokenKey, null)
        return dispatch({
          type: USER_LOGIN_FAIL,
          error: getLoginErrorFromServerResponse(userAuthResp),
        })
      }

      let userToken: any = null

      // JWT auth
      if (userAuthResp.jwt_token) {
        userToken = {
          jwttoken: userAuthResp.jwt_token,
        }
        api.jwtToken = userAuthResp.jwt_token
        const hostname = getUrlHostname(userAuthResp.user_data.url)

        if (hostname) {
          global.host = `https://${hostname}`
          api.host = `https://${hostname}`
        }
      }

      if (resetStateAfterLogin) dispatch(switchBusiness())
      // keep information if user logged in with MFA so we save on API call later
      const resultToken = (loginDetails as any).mfa_code
        ? { ...userToken, loggedInWithMFA: true }
        : userToken
      Storage.set(userTokenKey, resultToken)
      await dispatch(
        loadUser(returnPath, host, !!(loginDetails as any).mfa_code, {
          loginToken: resultToken,
        }),
      )
      // enforce 2fa setup only for normal login flow,
      // for invites user needs to set password first
      if (!('referer' in loginDetails)) checkMfaSetup(getState(), dispatch)
    } catch (error) {
      Storage.set(userTokenKey, null)
      return dispatch({
        type: USER_LOGIN_FAIL,
        error,
      })
    }
  }
}

/**
 * Switches to a different business context from settings page.
 * @param businessHostName - Hostname of the business to switch to
 * @param mobileUserToken - Token string for mobile authentication
 * @returns Thunk that handles business switching in settings
 */
export function businessSwitchInSettings(
  businessHostName: string,
  mobileUserToken: string,
): Thunk {
  return async (dispatch, getState) => {
    const { user } = getState()

    if (!mobileUserToken) return

    const hostname = `https://${businessHostName}`

    if (hostname) {
      global.host = hostname
      api.host = hostname
    }

    if (user.loggedInWithSAML) {
      await dispatch(
        loadUser('/', hostname, false, {
          loginType: 'saml',
        }),
      )
    } else {
      dispatch(switchBusiness())

      const resultToken = user.mfaCodeRequired
        ? { mobileUserToken: mobileUserToken, loggedInWithMFA: true }
        : { mobileUserToken: mobileUserToken }

      await dispatch(
        loadUser('/', hostname, user.mfaCodeRequired, {
          loginToken: resultToken,
        }),
      )
    }
  }
}

/**
 * Handles SAML login for users with multiple business accounts.
 * @param loginDetails - SAML login details
 * @returns Thunk that handles multi-account SAML authentication
 */
// SSO login for mobile
export function multiAccountSamlLogin(loginDetails: any): Thunk {
  return async dispatch => {
    dispatch({
      type: USER_LOGIN,
    })

    try {
      const userAuthResp = await api.multiAccountLogin(loginDetails)
      console.log('userAuth', userAuthResp)

      if (!userAuthResp.redirect_uri) {
        throw new Error('Invalid email')
      }

      return userAuthResp.redirect_uri
    } catch (error) {
      Storage.set(userTokenKey, null)
      dispatch({
        type: USER_LOGIN_FAIL,
        error,
      })
    }

    return null
  }
}
/**
 * Handles successful SAML authentication and user setup.
 * @param token - JWT token from successful SAML authentication
 * @returns Thunk that completes SAML login process
 */
export function samlLoginSuccess(token: string): Thunk {
  return async dispatch => {
    try {
      let userToken: any = null

      // JWT auth
      if (token) {
        userToken = {
          jwttoken: token,
        }
        api.jwtToken = token
        Storage.set(userTokenKey, userToken)
      }

      const { results } = await api.fetchUserMemberships()
      if (results?.length < 1) throw new Error('No active businesses found.')
      let hostname: string | null = null

      if (results?.length > 1) {
        return dispatch({
          type: USER_LOGIN_REQUIRE_BUSINESS_SELECT,
          businesses: results,
        })
      }

      if (results?.length === 1) hostname = `https://${results[0].hostname}`

      if (hostname) {
        global.host = hostname
        api.host = hostname
      }

      await dispatch(
        loadUser('/', hostname, false, {
          loginType: 'saml',
          loginToken: token,
        }),
      )
    } catch (error) {
      Storage.set(userTokenKey, null)
      return dispatch({
        type: USER_LOGIN_FAIL,
        error,
      })
    }
  }
}
/**
 * Updates user settings and optionally shows a notification.
 * @param settings - Partial settings object to update
 * @param showNotification - Whether to show success notification (default: false)
 * @returns Thunk that updates user settings
 */
export function updateUserSettings(
  settings: Partial<Settings>,
  showNotification = false,
): Thunk {
  return async dispatch => {
    dispatch({
      type: USER_SETTINGS_UPDATE,
    })

    try {
      const result = await api.updateUserSettings(settings)
      dispatch({
        type: USER_SETTINGS_UPDATE_SUCCESS,
        result,
      })

      if (showNotification) {
        pushUINotification({
          message: 'Settings updated!',
          level: 'success',
        })
      }
    } catch (error) {
      dispatch({
        type: USER_SETTINGS_UPDATE_FAIL,
        error,
      })
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      throw error
    }
  }
}
/**
 * Updates business settings with progress tracking and notifications.
 * @param settings - Business settings data to update
 * @param showNotifications - Whether to show success notifications (default: true)
 * @returns Thunk that updates business settings
 */
export function updateBusinessSettings(
  settings: BusinessDataPatchRequest,
  showNotifications = true,
): Thunk {
  return async (dispatch, getState) => {
    const { user } = getState()
    dispatch({
      type: BUSINESS_SETTINGS_UPDATE,
    })
    let result

    try {
      result = await api.updateBusinessSettings(settings, progress =>
        dispatch({
          type: BUSINESS_SETTINGS_UPDATE_PROGRESS,
          progress,
        }),
      )
      dispatch({
        type: BUSINESS_SETTINGS_UPDATE_SUCCESS,
        result: { ...user, business: result },
      })

      if (showNotifications) {
        pushUINotification({
          message: 'Settings updated!',
          level: 'success',
        })
      }
    } catch (error) {
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
      dispatch({
        type: BUSINESS_SETTINGS_UPDATE_FAIL,
        error,
      })
      throw error
    }
  }
}
/**
 * Updates business private metadata by merging with existing data.
 * @param data - Partial private metadata to merge
 * @returns Thunk that updates business private metadata
 */
export const updateBusinessPrivateMetadata =
  (data: Partial<PrivateMetadata>): Thunk =>
  (dispatch, getState) => {
    const business = getBusinessSettings(getState())
    return dispatch(
      updateUserBusiness({
        ...business,
        private_metadata: { ...business.private_metadata, ...data },
      } as BusinessData),
    )
  }
/**
 * Updates the user's business data in the application state.
 * @param business - Business data to update
 * @param successNotification - Optional success notification to display
 * @returns Thunk that updates user business data
 */
export function updateUserBusiness(
  business?: BusinessData,
  successNotification?: UINotification,
): Thunk {
  return (dispatch, getState) => {
    const { user } = getState()

    if (!business) {
      dispatch({
        type: BUSINESS_SETTINGS_UPDATE_FAIL,
      })
    }

    try {
      dispatch({
        type: BUSINESS_SETTINGS_UPDATE_SUCCESS,
        result: { ...user, business },
      })

      if (successNotification) {
        pushUINotification(successNotification)
      }
    } catch (error) {
      dispatch({
        type: BUSINESS_SETTINGS_UPDATE_FAIL,
        error,
      })
      throw error
    }
  }
}
let updateQueue = Promise.resolve()
/**
 * Saves the user's custom channel sort order to user settings.
 * @param channels - Array of channel objects with custom sort order
 * @returns Thunk that saves the user's sorted channels
 */
export function saveUserSortedChannels(channels: Array<any>): Thunk {
  return dispatch => {
    const result = {
      settings: {
        channels_custom_order: channels.map(c => c.id),
      },
    }
    dispatch({
      type: USER_SETTINGS_UPDATE_SUCCESS,
      result,
    })
    // wait for previous call
    updateQueue = updateQueue.then(() => {
      try {
        return api.updateUserSettings(result)
      } catch (error) {
        dispatch(sendGenericError(error))
      }
    })
  }
}
/**
 * Loads shortlist state from local storage for a specific user.
 * @param id - User ID to load state for
 * @param businessId - Optional business ID for React Native contexts
 * @returns Thunk that loads local shortlist state
 */
export function loadLocalShortlistState(
  id: UserId,
  businessId?: number,
): Thunk {
  return async dispatch => {
    const storageKey =
      __REACT_NATIVE__ && businessId
        ? `disco-${businessId}-${id}`
        : `disco-${id}`
    const savedState = await Storage.get(storageKey)
    if (savedState === null) return dispatch(startSavingShortlistState())
    return dispatch(initShortlists(savedState))
  }
}
/**
 * Loads business data with optional track count configuration.
 * @param TRACK_COUNT_TYPE - Optional type to control track counting behavior
 * @returns Thunk that loads business data from the server
 */
export const loadBusinessData =
  (
    TRACK_COUNT_TYPE?: string,
  ): ((dispatch: (...args: Array<any>) => any) => Promise<any>) =>
  async (dispatch: (...args: Array<any>) => any) => {
    try {
      const options =
        TRACK_COUNT_TYPE == 'WITHOUT_REALTIME_TRACK_COUNT'
          ? null
          : { realtime_track_count: 1 }

      const business = await api.getCurrentBusiness(options)
      return dispatch({
        type: BUSINESS_DATA_LOAD,
        result: business,
      })
    } catch (error) {
      return dispatch(sendGenericError(error))
    }
  }
/**
 * Loads business settings and constants from the server.
 * @returns Thunk that loads business settings
 */
export const loadBusinessSettings =
  (): ((dispatch: (...args: Array<any>) => any) => Promise<any>) =>
  async (dispatch: (...args: Array<any>) => any) => {
    try {
      const businessSettings = await api.getConstants()
      return dispatch({
        type: BUSINESS_SETTINGS_LOAD,
        result: businessSettings,
      })
    } catch (error) {
      console.log(error)
    }
  }
/**
 * Loads business subscription information if user has appropriate permissions.
 * @returns Thunk that loads subscription data for eligible users
 */
export const loadBusinessSubscription =
  (): ((
    dispatch: (...args: Array<any>) => any,
    getState: (...args: Array<any>) => any,
  ) => Promise<void>) =>
  async (
    dispatch: (...args: Array<any>) => any,
    getState: (...args: Array<any>) => any,
  ) => {
    const { user } = getState()
    const subscription_id =
      Boolean(user.business?.chargebee_subscription_id) ||
      Boolean(user.business?.private_metadata?.subscription_id)
    const subscriber =
      user.business?.private_metadata?.plan_type === 'subscriber' &&
      subscription_id
    const is_admin = user.is_admin
    if (!(is_admin && subscriber)) return
    dispatch({
      type: SUBSCRIPTION_LOAD,
    })

    try {
      const businessSubscription = await api.getCurrentBusinessSubscription()
      dispatch({
        type: SUBSCRIPTION_LOAD_SUCCESS,
        result: businessSubscription,
      })
    } catch (error) {
      dispatch({
        type: SUBSCRIPTION_LOAD_FAIL,
        error,
      })
      console.log(error)
    }
  }
/**
 * Clears any existing user login error from the state.
 * @returns Action object to clear login error
 */
export const clearUserLoginError = (): {
  type: string
} => ({
  type: USER_LOGIN_ERROR_CLEAR,
})

type UserMembershipsResponse = {
  results: Business[]
}

export function reloadUserBusinesses(): Thunk {
  return async dispatch => {
    try {
      const response: UserMembershipsResponse = await api.fetchUserMemberships(
        true,
      )

      dispatch({
        type: USER_UPDATED_MOBILE_BUSINESS_LIST,
        // match the mobile businesses object
        businesses: response.results.map(b => {
          return {
            name: b.name,
            hostname: b.hostname,
            logo: b.image,
          }
        }),
      })
    } catch (error) {
      captureError(error)
    }
  }
}
